import { useMemo } from "react";
import { getLangToDisplay, strings } from "@/I18n";
import {
  convertForHint,
  formatAreaSquareMeters,
  formatDistanceMeters,
  type HintSourceUnit,
  type MeasurementUnit,
  type Quantity,
} from "@/v2/utils/measurement";
import { useMeasurementSystem } from "./useMeasurementSystem";

const UNIT_LABEL_KEYS: Record<MeasurementUnit, string> = {
  m: "units.m",
  ft: "units.ft",
  mi: "units.mi",
  mph: "units.mph",
  m2: "units.m2",
  ha: "units.ha",
  ft2: "units.ft2",
  ac: "units.ac",
};

export function quantityLabel(quantity: Quantity): string {
  return `${quantity.value} ${strings(UNIT_LABEL_KEYS[quantity.unit])}`;
}

/** Read-out formatters bound to the configured display units and the dashboard locale. */
export function useMeasurementFormatter() {
  const system = useMeasurementSystem();
  const locale = getLangToDisplay();

  return useMemo(
    () => ({
      formatDistanceMeters: (meters: number) =>
        quantityLabel(formatDistanceMeters(meters, system, locale)),
      formatAreaSquareMeters: (squareMeters: number) =>
        quantityLabel(formatAreaSquareMeters(squareMeters, system, locale)),
      hint: (value: number | null | undefined, from: HintSourceUnit) => {
        const converted = convertForHint(value, from, system, locale);
        return converted == null
          ? null
          : strings("units.convertedHint", { value: quantityLabel(converted) });
      },
    }),
    [system, locale]
  );
}
